import * as React from 'react';
import {
  Button as FreshButton,
  Drawer,
  IDrawerProps,
} from '@revfluence/fresh';
import { XmarkIcon } from '@revfluence/fresh-icons/solid/esm';
import { isFunction } from 'lodash';

import styles from '@frontend/applications/TermsApp/components/shared/Wizard/TermsWizard.scss';
import { useGetTermsName } from '@frontend/app/hooks';

// Import FilterDrawer instead of Sheet
import { FilterDrawer } from '@frontend/app/containers/ProductFulfillmentDashboard/FilterDrawer';
import TermsTemplate from '@frontend/app/containers/Projects/TermsPage/components/TermsTemplate';
import { TermsType } from '@frontend/app/types/globalTypes';

// Basic Terms Drawer using Fresh UI for backward compatibility
const OldTermsDrawer: React.FC<IDrawerProps> = (props) => {
  const {
    children,
    onClose,
    title,
    ...remainingProps
  } = props;
  const { singularTermsName } = useGetTermsName();
  const closeButton = () => {
    if (!isFunction(onClose)) {
      return null;
    }

    return (
      <FreshButton
        icon={<XmarkIcon />}
        onClick={onClose}
        type="text"
      />
    );
  };

  return (
    <Drawer
      bodyStyle={{ padding: 0 }}
      className={styles.TermsWizard}
      closable={false}
      width={1024}
      onClose={onClose}
      {...remainingProps}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.headerContentWrapper} />
          <div className={styles.headerContentWrapper}>
            <img
              alt={`Send ${singularTermsName}`}
              className={styles.titleIcon}
              src="https://storage.googleapis.com/aspirex-static-files/terms.svg"
            />
            {title}
          </div>
          <div className={styles.headerContentWrapper}>
            {closeButton()}
          </div>
        </div>
        {children}
      </div>
    </Drawer>
  );
};

const ShadcnTermsDrawer: React.FC<IDrawerProps> = (props) => {
  const {
    children,
    onClose,
    title,
    open,
  } = props;

  // Convert IDrawerProps onClose to FilterDrawer's onOpenChange
  const handleOpenChange = (newOpen: boolean) => {
    if (onClose && !newOpen) {
      // Only trigger onClose when drawer is closing
      onClose(new Event('close') as any);
    }
  };

  // Convert title to string if it's not already
  const titleString = title !== undefined ? String(title) : '';

  return (
    <FilterDrawer
      open={!!open}
      onOpenChange={handleOpenChange}
      headerProps={{
        title: titleString,
      }}
      width="576px"
    >
      {children}
    </FilterDrawer>
  );
};

export interface TermsDrawerProps extends IDrawerProps {
  projectId?: number;
  termsType?: TermsType;
  onSwitchTermsType?: () => void;
  showFlexUI?: boolean;
}

export const TermsDrawer: React.FC<TermsDrawerProps> = (props) => {
  const {
    projectId,
    termsType,
    onSwitchTermsType,
    children,
    onClose,
    showFlexUI,
    ...drawerProps
  } = props;

  // Create a wrapper for the onClose function to match TermsTemplate's expected signature
  const handleClose = React.useCallback(() => {
    if (onClose) {
      onClose(new Event('close') as any);
    }
  }, [onClose]);

  // If children are provided, just use them
  if (children) {
    return showFlexUI
      ? <ShadcnTermsDrawer onClose={onClose} {...drawerProps}>{children}</ShadcnTermsDrawer>
      : <OldTermsDrawer onClose={onClose} {...drawerProps}>{children}</OldTermsDrawer>;
  }

  // Otherwise, render TermsTemplate
  return showFlexUI
    ? (
      <ShadcnTermsDrawer onClose={onClose} {...drawerProps}>
        <TermsTemplate
          isEditing={false}
          memberIds={[]}
          projectId={projectId}
          termsType={termsType}
          onClose={handleClose}
          onSwitchTermsType={onSwitchTermsType || (() => {})}
        />
      </ShadcnTermsDrawer>
    )
    : (
      <OldTermsDrawer onClose={onClose} {...drawerProps}>
        <TermsTemplate
          isEditing={false}
          memberIds={[]}
          projectId={projectId}
          termsType={termsType}
          onClose={handleClose}
          onSwitchTermsType={onSwitchTermsType || (() => {})}
        />
      </OldTermsDrawer>
    );
};
